import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtDateShort, fmtTimeRange } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HoursInput } from "@/components/attendance/HoursInput";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Save, Check, X, Clock, FileText, Search, QrCode, UserPlus, Pencil } from "lucide-react";
import { SessionEditDialog } from "@/components/attendance/SessionEditDialog";
import { useAuth } from "@/lib/auth";
import { Link as RouterLink } from "react-router-dom";
import { TRACK_SHORT, sessionYearsLabel } from "@/lib/school";
import { YearLevelsPicker } from "@/components/attendance/YearLevelsPicker";
import { StudentPicker } from "@/components/StudentPicker";
import StudentAvatar from "@/components/StudentAvatar";
import { JustifiedWindow } from "@/components/attendance/JustifiedWindow";

function AddStudentBox({ sessionId, existingIds, track }) {
  const qc = useQueryClient();
  const [sid, setSid] = useState("");
  const tt = track === "modulare" ? "modulare" : "residenziale";
  const { data: students = [] } = useQuery({
    queryKey: ["students-active-min", tt],
    queryFn: async () => (await api.get("/students", { params: { status: "attivo", track_type: tt } })).data,
  });
  const available = students.filter((s) => !existingIds.includes(s.id));
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/attendance/sessions/${sessionId}/students`, { student_id: sid })).data,
    onSuccess: () => { toast.success("Studente aggiunto al registro"); setSid(""); qc.invalidateQueries({ queryKey: ["attendance-session", sessionId] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  return (
    <div className="flex items-center gap-2 w-full sm:w-auto" data-testid="add-student-box">
      <div className="flex-1 sm:w-72"><StudentPicker students={available} value={sid} onChange={setSid} testId="add-student-picker" placeholder="Aggiungi studente al registro…"/></div>
      <Button size="sm" disabled={!sid || mut.isPending} onClick={() => mut.mutate()} className="bg-[#1C352D] hover:bg-[#264A3E]" data-testid="add-student-confirm"><UserPlus size={14} className="mr-1"/> Aggiungi</Button>
    </div>
  );
}

function DurationEditor({ session }) {
  const qc = useQueryClient();
  const [h, setH] = useState(session.duration_hours || 1);
  useEffect(() => { setH(session.duration_hours || 1); }, [session.duration_hours]);
  const mut = useMutation({
    mutationFn: async () => (await api.patch(`/attendance/sessions/${session.id}`, { duration_hours: Number(h) })).data,
    onSuccess: () => { toast.success("Durata aggiornata: le percentuali di frequenza sono ricalcolate"); qc.invalidateQueries({ queryKey: ["attendance-session", session.id] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const changed = Number(h) !== Number(session.duration_hours || 1);
  return (
    <div className="mt-3 flex items-center gap-2 text-sm" data-testid="session-duration-editor">
      <span className="text-stone-500">Ore conteggiate per chi è presente:</span>
      <HoursInput value={h} onChange={setH} className="h-8 w-20" testId="session-duration-input"/>
      <span className="text-stone-500">h</span>
      {changed && <Button size="sm" disabled={!(Number(h) > 0 && Number(h) <= 24) || mut.isPending} onClick={() => mut.mutate()} className="h-8 bg-[#1C352D] hover:bg-[#264A3E]" data-testid="session-duration-save">Salva</Button>}
    </div>
  );
}

function YearsEditor({ session }) {
  const qc = useQueryClient();
  const initial = session.year_levels ?? (session.year_level ? [session.year_level] : []);
  const [ys, setYs] = useState(initial);
  useEffect(() => { setYs(session.year_levels ?? (session.year_level ? [session.year_level] : [])); }, [session.year_levels, session.year_level]);
  const mut = useMutation({
    mutationFn: async () => (await api.patch(`/attendance/sessions/${session.id}`, { year_levels: ys })).data,
    onSuccess: (r) => {
      toast.success(r.added_students ? `Anni aggiornati: ${r.added_students} studenti aggiunti come assenti` : "Anni aggiornati");
      qc.invalidateQueries({ queryKey: ["attendance-session", session.id] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const changed = [...ys].sort().join() !== [...initial].sort().join();
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 text-sm" data-testid="session-years-editor">
      <span className="text-stone-500">Anni in aula:</span>
      <YearLevelsPicker value={ys} onChange={setYs} testId="session-years-edit"/>
      {changed && <Button size="sm" disabled={mut.isPending} onClick={() => mut.mutate()} className="h-8 bg-[#1C352D] hover:bg-[#264A3E]" data-testid="session-years-save">Salva</Button>}
    </div>
  );
}

const STATUS_OPTIONS = [
  { key: "presente", label: "Presente", short: "P", icon: Check, className: "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100", activeClassName: "bg-emerald-600 text-white border-emerald-600" },
  { key: "assente",  label: "Assente",  short: "A", icon: X, className: "bg-red-50 text-red-800 border-red-200 hover:bg-red-100", activeClassName: "bg-red-600 text-white border-red-600" },
  { key: "ritardo",  label: "Ritardo",  short: "R", icon: Clock, className: "bg-orange-50 text-orange-800 border-orange-200 hover:bg-orange-100", activeClassName: "bg-orange-500 text-white border-orange-500" },
  { key: "giustificato", label: "Giustificato", short: "G", icon: FileText, className: "bg-stone-100 text-stone-700 border-stone-200 hover:bg-stone-200", activeClassName: "bg-stone-700 text-white border-stone-700" },
];

export default function AttendanceSession() {
  const { id } = useParams();
  const { user } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const canEditSession = ["admin", "direzione", "segreteria"].includes(user?.role);
  const location = useLocation();
  const isDocentePath = location.pathname.startsWith("/docente/");
  const backTo = isDocentePath ? "/docente/presenze" : "/scuola/presenze";
  const qrTo = isDocentePath ? `/docente/presenze/${id}/qr` : `/scuola/presenze/${id}/qr`;
  const qc = useQueryClient();
  const [rows, setRows] = useState({}); // student_id -> {status, note, arrival_time}
  const [dirty, setDirty] = useState(false);
  const [query, setQuery] = useState("");

  const { data: session, isLoading } = useQuery({
    queryKey: ["attendance-session", id],
    queryFn: async () => (await api.get(`/attendance/sessions/${id}`)).data,
  });

  useEffect(() => {
    if (session?.records) {
      const map = {};
      for (const r of session.records) {
        map[r.student_id] = {
          record_id: r.id,
          status: r.status,
          note: r.note || "",
          arrival_time: r.arrival_time || "",
          justified_from: r.justified_from || "",
          justified_to: r.justified_to || "",
          student_name: r.student_name,
          matricola: r.matricola,
          photo_path: r.photo_path,
          course_track: r.course_track,
        };
      }
      setRows(map);
      setDirty(false);
    }
  }, [session?.id, session?.records]);

  const orderedIds = useMemo(() => {
    const ids = Object.keys(rows);
    ids.sort((a, b) => (rows[a].student_name || "").localeCompare(rows[b].student_name || ""));
    if (!query) return ids;
    const q = query.toLowerCase();
    return ids.filter((sid) => {
      const r = rows[sid];
      return (r.student_name || "").toLowerCase().includes(q) || (r.matricola || "").toLowerCase().includes(q);
    });
  }, [rows, query]);

  const counts = useMemo(() => {
    const c = { presente: 0, assente: 0, ritardo: 0, giustificato: 0 };
    Object.values(rows).forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const total = Object.keys(rows).length;
  const rate = total ? ((counts.presente + counts.ritardo) / total * 100) : 0;

  function updateRow(studentId, changes) {
    setRows((prev) => ({ ...prev, [studentId]: { ...prev[studentId], ...changes } }));
    setDirty(true);
  }

  function markAll(status) {
    setRows((prev) => {
      const next = {};
      for (const [k, v] of Object.entries(prev)) next[k] = { ...v, status };
      return next;
    });
    setDirty(true);
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      const records = Object.entries(rows).map(([student_id, r]) => ({
        student_id,
        status: r.status,
        note: r.note || null,
        arrival_time: r.arrival_time || null,
        ...((["assente", "ritardo"].includes(r.status) && r.justified_from && r.justified_to)
          ? { justified_from: r.justified_from, justified_to: r.justified_to } : {}),
      }));
      const bad = records.find((r) => r.justified_from && r.justified_from >= r.justified_to);
      if (bad) throw new Error("Orario giustificato non valido: l'inizio deve precedere la fine");
      return (await api.post("/attendance/records/bulk", { session_id: id, records })).data;
    },
    onSuccess: () => {
      toast.success("Presenze salvate");
      qc.invalidateQueries({ queryKey: ["attendance-session", id] });
      qc.invalidateQueries({ queryKey: ["attendance-sessions"] });
      qc.invalidateQueries({ queryKey: ["attendance-overview"] });
      setDirty(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || e.message || "Errore"),
  });

  if (isLoading || !session) return <div className="p-10 text-stone-500 text-sm">Caricamento…</div>;

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <Link to={backTo} className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-4">
        <ArrowLeft size={13}/> Torna alle sessioni
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500">
            {fmtDateShort(session.date)}
            {session.start_time ? ` · ore ${fmtTimeRange(session.start_time, session.end_time)}` : ""}
            {sessionYearsLabel(session) ? ` · ${sessionYearsLabel(session)}` : ""}
            {session.track_type ? ` · ${TRACK_SHORT[session.track_type]}` : ""}
            {session.course_track ? ` · ${session.course_track}` : ""}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-[#1C352D]">{session.title}</h1>
          {session.course_name && (
            <div className="mt-2">
              <Badge className={`text-[10px] rounded-full ${session.is_seminar ? "bg-[#E2725B]/10 text-[#8f3e26] border-[#E2725B]/30" : "bg-emerald-50 text-emerald-800 border-emerald-200"} border`}>
                {session.is_seminar ? "SEMINARIO" : "CORSO"} · {session.course_name}
                {session.course_cfu != null && ` · ${session.course_cfu} CFU`}
              </Badge>
            </div>
          )}
          {!session.course_id && (
            <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 inline-flex items-start gap-1.5" data-testid="legacy-session-banner">
              <span className="text-base leading-none">⚠️</span>
              <span>Sessione generica non collegata a un corso. Le presenze di questa lezione non concorrono alle statistiche per corso.</span>
            </div>
          )}
          {session.notes && <div className="text-sm text-stone-600 mt-1">{session.notes}</div>}
          <DurationEditor session={session}/>
          <YearsEditor session={session}/>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {canEditSession && (
            <Button variant="outline" className="rounded-full" onClick={() => setEditOpen(true)} data-testid="session-edit-btn">
              <Pencil size={14} className="mr-1.5"/> Modifica sessione
            </Button>
          )}
          {canEditSession && <SessionEditDialog session={session} open={editOpen} onOpenChange={setEditOpen}/>}
          <RouterLink to={qrTo}>
            <Button
              data-testid="show-qr-btn"
              variant="outline"
              className="rounded-full border-[#1C352D] text-[#1C352D] hover:bg-[#1C352D] hover:text-white transition-colors duration-150"
            >
              <QrCode size={14} className="mr-1.5"/> Mostra QR check-in
            </Button>
          </RouterLink>
          <div className="flex items-center gap-1 border border-stone-200 rounded-full pl-3 pr-1 py-1 bg-white">
            <span className="text-[10px] uppercase tracking-widest text-stone-500">Segna tutti</span>
            {STATUS_OPTIONS.map(({ key, short, activeClassName }) => (
              <button
                key={key}
                onClick={() => markAll(key)}
                data-testid={`markall-${key}`}
                className={`w-7 h-7 rounded-full text-[11px] font-semibold ${activeClassName} transition-transform duration-150 hover:scale-105`}
                title={`Segna tutti come ${key}`}
              >
                {short}
              </button>
            ))}
          </div>
          <Button
            data-testid="save-attendance-btn"
            disabled={!dirty || saveMut.isPending}
            onClick={() => saveMut.mutate()}
            className="rounded-full bg-[#E2725B] hover:bg-[#c85d47] text-white transition-colors duration-150"
          >
            <Save size={14} className="mr-1.5"/>{saveMut.isPending ? "Salvataggio…" : dirty ? "Salva presenze" : "Tutto salvato"}
          </Button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Tot iscritti</div>
          <div className="text-xl font-semibold mt-1">{total}</div>
        </Card>
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700">Presenti</div>
          <div className="text-xl font-semibold mt-1">{counts.presente}</div>
        </Card>
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-red-700">Assenti</div>
          <div className="text-xl font-semibold mt-1">{counts.assente}</div>
        </Card>
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-orange-700">Ritardi</div>
          <div className="text-xl font-semibold mt-1">{counts.ritardo}</div>
        </Card>
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-700">Tasso</div>
          <div className="text-xl font-semibold mt-1">{rate.toFixed(0)}%</div>
        </Card>
      </div>

      {/* Roster */}
      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 border-b border-stone-100 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="text-sm font-medium">Registro</div>
          <AddStudentBox sessionId={id} existingIds={Object.keys(rows)} track={session.track_type}/>
          <div className="relative flex-1 max-w-md sm:ml-auto w-full">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input
              data-testid="roster-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per nome o matricola…"
              className="pl-9"
            />
          </div>
        </div>
        {orderedIds.length === 0 ? (
          <div className="p-8 text-center text-stone-500 text-sm">
            {total === 0 ? (
              <>Nessuno studente attivo. Aggiungi studenti dalla sezione <Link to="/scuola/studenti" className="text-[#E2725B] underline">Studenti</Link>.</>
            ) : "Nessun risultato per la ricerca."}
          </div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {orderedIds.map((sid, i) => {
              const r = rows[sid];
              return (
                <li key={sid} className="p-4 flex flex-wrap items-center gap-4 animate-in-row" style={{ animationDelay: `${i*4}ms` }} data-testid={`roster-row-${sid}`}>
                  <StudentAvatar studentId={sid} photoPath={r.photo_path} name={r.student_name} className="w-10 h-10 rounded-lg text-xs" testId={`roster-avatar-${sid}`}/>
                  <div className="min-w-[160px] flex-1">
                    <div className="font-medium truncate">
                      <Link to={`/scuola/studenti/${sid}`} className="hover:underline">{r.student_name}</Link>
                    </div>
                    <div className="text-[11px] text-stone-500 mono">{r.matricola}{r.course_track ? ` · ${r.course_track}` : ""}</div>
                  </div>

                  {r.status === "ritardo" && (
                    <Input
                      type="time"
                      value={r.arrival_time || ""}
                      onChange={(e) => updateRow(sid, { arrival_time: e.target.value })}
                      className="w-24 h-9 text-xs"
                      data-testid={`arrival-${sid}`}
                    />
                  )}
                  {["assente", "ritardo"].includes(r.status) && (
                    <JustifiedWindow key={`${sid}-${r.record_id}`} sid={sid} from={r.justified_from} to={r.justified_to}
                      defFrom={session.start_time} defTo={r.status === "ritardo" && r.arrival_time ? r.arrival_time : session.end_time}
                      onChange={(c) => updateRow(sid, c)} />
                  )}
                  <Input
                    value={r.note}
                    onChange={(e) => updateRow(sid, { note: e.target.value })}
                    placeholder={r.status === "giustificato" ? "Motivo giustificazione" : "Nota"}
                    className="w-56 h-9 text-xs hidden md:block"
                    data-testid={`note-${sid}`}
                  />

                  <div className="flex gap-1">
                    {STATUS_OPTIONS.map(({ key, short, label, className, activeClassName }) => {
                      const active = r.status === key;
                      return (
                        <button
                          key={key}
                          onClick={() => updateRow(sid, { status: key })}
                          data-testid={`status-${sid}-${key}`}
                          className={`w-9 h-9 rounded-lg border text-xs font-semibold transition-all duration-150 ${active ? activeClassName + " scale-105" : className}`}
                          title={label}
                        >
                          {short}
                        </button>
                      );
                    })}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {dirty && (
        <div className="fixed bottom-6 right-6 flex items-center gap-3 bg-white border border-stone-200 shadow-lg rounded-full px-4 py-2 z-40">
          <Badge className="bg-orange-100 text-orange-800 border border-orange-200 rounded-full text-[10px]">Non salvato</Badge>
          <Button
            size="sm"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
            className="rounded-full bg-[#E2725B] hover:bg-[#c85d47] text-white h-8"
          >
            <Save size={12} className="mr-1.5"/>Salva
          </Button>
        </div>
      )}
    </div>
  );
}
